import React from 'react';
import { Platform } from 'react-native';
import { AuthProvider, useAuth } from './src/hooks/useAuth';
import { useLicense } from './src/hooks/useLicense';
import AuthScreen from './src/components/AuthScreen';
import LicenseIssueScreen from './src/components/LicenseIssueScreen';
import AppContent from './src/AppContent';
import { ToastContainer } from 'react-toastify';
import { Loading } from './src/components/Loading';
import { initializeAppInsights } from './src/utils/appInsights';
import { initializeMixpanel } from './src/utils/mixpanel';
import { FeatureFlagsProvider } from './src/featureFlags/FeatureFlagsContext';

// Initialize Application Insights
initializeAppInsights();
// Initialize Mixpanel
initializeMixpanel();

function Entry() {
  const { token, loading: authLoading } = useAuth();
  const {
    license,
    initialLoading: licLoading,
    initialError: licError,
  } = useLicense();

  const LEMON_SQUEEZY_SIGNUP_URL =
    process.env.LEMON_SQUEEZY_SIGNUP_URL ||
    'https://vistaventures.lemonsqueezy.com/checkout';

  const parseReadableMessage = (value: any): string => {
    if (!value) return 'We could not validate your license. Please finish checkout to continue.';
    const fallback = typeof value === 'string' ? value : value.message || String(value);

    const tryJsonString = (input: string): string | null => {
      try {
        const parsed = JSON.parse(input);
        if (parsed && typeof parsed === 'object') {
          if (parsed.message) return parsed.message;
          if (parsed.error) return parsed.error;
        }
      } catch {
        return null;
      }
      return null;
    };

    if (typeof value === 'string') {
      const parsed = tryJsonString(value);
      if (parsed) return parsed;
      return value;
    }

    if (value.message && typeof value.message === 'string') {
      const parsed = tryJsonString(value.message);
      if (parsed) return parsed;
      return value.message;
    }

    return fallback;
  };

  const handleContinueCheckout = async () => {
    if (Platform.OS === 'web') {
      window.location.href = LEMON_SQUEEZY_SIGNUP_URL;
    } else {
      const { Linking } = require('react-native');
      await Linking.openURL(LEMON_SQUEEZY_SIGNUP_URL);
    }
  };

  // 1) authenticating?
  if (authLoading) {
    return <div>
      <Loading />
    </div>;
  }

  // 2) not signed in?
  if (!token) {
    return <AuthScreen />;
  }

  // 3) still doing the *first* license fetch?
  if (licLoading) {
    return <Loading />
  }

  // 4) that first license fetch errored?
  if (licError) {
    return (
      <LicenseIssueScreen
        title="License Error"
        message={parseReadableMessage(licError)}
        onContinueCheckout={handleContinueCheckout}
      />
    );
  }

  // 5) we have a license; inactive plan?
  if (license?.status !== 'active') {
    const statusLabel = license?.status || 'unknown';
    return (
      <LicenseIssueScreen
        title="Access Denied"
        message={`Your plan is “${statusLabel}”. Please complete checkout to activate access.`}
        onContinueCheckout={handleContinueCheckout}
      />
    );
  }

  // 6) everything’s good—show the real app
  return <AppContent />;
}

export default function App() {
  return (
    <AuthProvider>
      <FeatureFlagsProvider>
        <Entry />
        <ToastContainer />
      </FeatureFlagsProvider>
    </AuthProvider>
  );
}
